import type { EconomySeries } from "../lib/economy-curves";
import { useRef, useState } from "react";
import { Button } from "../components/ui/Button";
import { CellInput, Hint, Input } from "../components/ui/Field";
import { Notice } from "../components/ui/Notice";
import { Panel, SectionTitle, Toolbar } from "../components/ui/Panel";
import { Tag } from "../components/ui/Tag";
import { Thumb } from "../components/ui/Thumb";
import { DataTable, type Column } from "../components/data/DataTable";
import { CheckCell, CostSourceBar, InUseTag, RivalHeader } from "../components/data/CostSourceBar";
import { useAssetUpload } from "../components/data/useAssetUpload";
import { fmt } from "../lib/model";
import { sumBigPartial, type Money } from "../lib/big";
import { Amount } from "../components/ui/Amount";
import {
  defaultStarEcon, skinOf, starChecks, starIssue,
  type PettownSlot, type PettownStar, type PettownStarView,
} from "../lib/pettown";
import { costSourceKind, costSourceRival } from "../lib/costSource";
import type { RivalDef } from "../lib/fixtures";
import { useT } from "../lib/i18n";
import { EconModelNotice, GMilliCell } from "../components/data/GMilliCell";
import { suggestGMilli } from "../lib/g-milli";


/**
 * 主建筑星级表 = 萌宠小镇唯一的造价表（设计稿 newbuildingpage.jsx 的 MainBuildingPage）。
 *
 * 一行一颗星，就两个输入：目标骰次定升这颗星要掷几次，手填价定这颗星花多少钱。
 * 公式价 / 手填价 / 各竞品价并排，客户端在用的那列打标；校验按在用的那套逐星判。
 * 星数由「累计星数上限」带出，这页不增删星，也没有任何分组——星是唯一的配置单位（leo 2026-09-17）。
 * 另一样逐星的东西是外观（留空沿用之前最近配过的那一颗）。一颗星就是一格（leo 2026-09-17），这页不出现格数。
 */
export function PettownStarPage({
  stars, slots, editable, onPatch, onRivalPrice, onApplyPrices, incomeSeries,
  rivals, costSource, onSource, onAddRival, onRenameRival, onRemoveRival, econ,
}: {
  /**
   * 本版本的金额口径（docs/economy-g-model.md §2.4）与各星的 g（星号 → 千分之一金币的十进制串，取自覆盖行）。
   * model 2 时多一列「单掷金币 g」，经 onPatch({ gMilli }) 改；缺省按 model 1
   */
  econ?: { model: 1 | 2; scale?: { num: string; den: string } | null; legacyEMilli?: number | null; gByStar: Record<number, string> };
  /** 完整视图：1..累计星数上限，每星一行，已按在用的那套算好 price */
  stars: PettownStarView[];
  incomeSeries: EconomySeries[];
  onApplyPrices: (rows:PettownStarView[]) => void;
  /** 只用来显示「这一星解锁哪几栋」，不改它 */
  slots: PettownSlot[];
  editable: boolean;
  /** 按星号改覆盖（没有覆盖行就新插一行） */
  onPatch: (star: number, patch: Partial<Pick<PettownStar, "skinUrl" | "manualPrice" | "targetRolls" | "gMilli">>) => void;
  onRivalPrice: (rivalId: string, star: number, v: Money | null) => void;
  rivals: RivalDef[];
  costSource: string;
  onSource: (v: string) => void;
  onAddRival: () => void;
  onRenameRival: (r: RivalDef) => void;
  onRemoveRival: (r: RivalDef) => void;
}) {
  const t = useT();
  // 不翻页（leo 2026-09-17）：全部星一张表往下滚；「跳到星」只是把那一行滚到眼前
  const [jump, setJump] = useState("");
  const [from,setFrom]=useState("");
  const [to,setTo]=useState("");
  const [onlyBad,setOnlyBad]=useState(false);
  const [view,setView]=useState("cost");
  const showB = view === "b" && econ?.model !== 2;
  const tableRef = useRef<HTMLDivElement>(null);
  const scrollToStar = (n: number) => {
    const star = Math.min(Math.max(1, Math.floor(n) || 1), Math.max(1, stars.length));
    const el = tableRef.current;
    if (!el) return;
    // 表是窗口化的（远处的行没挂），按行高算位置：合计行（单独量，比数据行矮）之后第 n 颗星在第 n 行
    const rowH = el.querySelector("tbody tr[data-row]")?.getBoundingClientRect().height || 47;
    const sumH = el.querySelector("tbody tr:not([data-row]):not([aria-hidden])")?.getBoundingClientRect().height ?? 0;
    const head = el.querySelector("thead")?.getBoundingClientRect().height ?? 0;
    const top = el.getBoundingClientRect().top + window.scrollY + head + sumH + rowH * Math.max(0, shown.findIndex(r=>r.star===star));
    window.scrollTo({ top: Math.max(0, top - window.innerHeight / 2) });
  };
  // module=pettown：萌宠小镇的图归自己的 CDN 目录，别混进关卡 / 卡牌那几个
  const up = useAssetUpload<number>({ module: "pettown" });
  const kind = costSourceKind(costSource);
  const inUseRival = costSourceRival(costSource);

  const checks = starChecks(stars, costSource);
  const checkOf = (r: PettownStarView) => checks[r.star - 1];
  const failed = checks.filter((c) => !c.ok).length;
  // 星数与星价都没有上限：合计按 bigint 加
  const total = sumBigPartial(stars.map((r) => r.price));
  const rollsTotal = sumBigPartial(stars.map((r) => r.rolls));
  const changedRolls = stars.filter((r) => !r.rollsDefault).length;
  const missing = stars.filter((r) => r.missing).length;
  const sumOf = (pick: (r: PettownStarView) => bigint | null | undefined) => sumBigPartial(stars.map(pick));
  const filledOf = (pick: (r: PettownStarView) => bigint | null | undefined) => stars.filter((r) => pick(r) != null).length;
  const cellBad = (r: PettownStarView, id: string) => (id === "manual" ? kind === "manual" : inUseRival === id) && !checkOf(r).ok;
  const dash = <span className="text-ink-placeholder">—</span>;
  const reasonOf = (r: PettownStarView): string => {
    const c = checkOf(r);
    if (c.reason) return t(c.reason, c.vars);
    if (kind === "formula") return t("pt.check.formulaOk");
    return r.missing ? t("pt.check.usesFormula") : t("pt.check.noDrop");
  };
  /** 合计行里「某一套」的合计：填了几颗、没填的按公式 */
  const overlaySum = (pick: (r: PettownStarView) => bigint | null | undefined) => {
    const n = filledOf(pick);
    return n
      ? (
        <div className="flex flex-col items-end">
          <Amount v={sumOf((r) => pick(r) ?? r.formula)} />
          <span className="text-[11px] font-normal text-ink-placeholder">{t("pt.sum.filled", { n })}</span>
        </div>
      )
      : (
        <div className="flex flex-col items-end">
          <span>—</span>
          <span className="text-[11px] font-normal text-ink-placeholder">{t("pt.sum.none")}</span>
        </div>
      );
  };

  const shown=stars.filter(r=>(!from||r.star>=Number(from))&&(!to||r.star<=Number(to))&&(!onlyBad||!checkOf(r).ok||starIssue(r)!=null));
  const columns: Column<PettownStarView>[] = [
    {
      key: "star", api: "ptStar.star", title: t("pt.col.star"), width: 90, stickyLeft: 0,
      render: (r) => <span className="font-semibold">{t("pt.starNo", { n: r.star })}</span>,
    },
    {
      key: "rolls", api: "ptStar.rolls", width: 130, align: "right", tint: "editable",
      title: <span className="inline-flex items-center text-primary">{t("pt.col.rolls")}<Hint text={t("pt.col.rolls.hint")} /></span>,
      sub: t("pt.col.rolls.sub"),
      cellClass: (r) => (r.bad ? "!bg-danger-50" : undefined),
      render: (r) => (
        <div className="flex flex-col items-end gap-0.5">
          <CellInput exact value={r.rolls} disabled={!editable} tone={r.rolls > 0n ? undefined : "danger"}
            onChange={(v) => onPatch(r.star, { targetRolls: v })} />
          <span className="text-[11px] text-ink-placeholder">{t("pt.econ.estimate")}: {r.estimate?.toString()??"—"}</span>
          <span className={`text-[11px] leading-tight ${r.rollsDefault ? "text-ink-placeholder" : "text-warning"}`}>
            {r.rollsDefault ? t("pt.rolls.default") : t("pt.rolls.changed", { v: defaultStarEcon(r.star).rolls })}
          </span>
        </div>
      ),
    },
    ...(econ?.model === 2 ? [{
      key: "g", width: 150, align: "right" as const, tint: "editable" as const,
      title: <span className="inline-flex items-center text-primary">{t("econ.g.col")}<Hint text={t("econ.g.hintStar")} /></span>,
      render: (r: PettownStarView) => <GMilliCell value={econ.gByStar[r.star] ?? null} disabled={!editable} placeholder={t("econ.g.curve")}
        suggest={suggestGMilli(stars.map((s) => econ.gByStar[s.star]), stars.findIndex((s) => s.star === r.star) + 1)}
        onChange={(g) => onPatch(r.star, { gMilli: g })} />,
    }] : []),
    {
      key: "formula", api: "ptStar.formula", width: 140, align: "right", tint: "readonly",
      title: <span className="inline-flex items-center">{t("pt.star.formula")}<Hint text={t(econ?.model === 2 ? "pt.star.formula.hintG" : "pt.star.formula.hint")} /><InUseTag on={kind === "formula"} /></span>,
      sub: <span className="flex items-center justify-end gap-2">{t("pt.star.formula.sub")}<Button size="sm" disabled={!editable||!stars.some(r=>!r.bad)} onClick={()=>onApplyPrices(stars)}>{t("cost.applyAlgoAll")}</Button></span>,
      render: (r) => <div className="flex items-center justify-end gap-2">{r.bad ? dash : <Amount className="text-ink-secondary" v={r.formula} />}<Button size="sm" disabled={!editable||r.bad} aria-label={t("pt.econ.applyOne",{n:r.star})} onClick={()=>onApplyPrices([r])}>→</Button></div>,
    },
    {
      key: "manual", api: "ptStar.manual", width: 150, align: "right", tint: "editable",
      title: <span className="inline-flex items-center text-primary">{t("pt.star.manual")}<InUseTag on={kind === "manual"} /></span>,
      sub: t("pt.star.manual.sub"),
      cellClass: (r) => (cellBad(r, "manual") ? "!bg-danger-50" : undefined),
      render: (r) => (
        <CellInput exact value={r.manual} disabled={!editable} placeholder={r.bad ? t("pt.star.pending") : t("pt.star.manualPh", { v: fmt(r.formula) })}
          onChange={(v) => onPatch(r.star, { manualPrice: v })} />
      ),
    },
    ...rivals.map<Column<PettownStarView>>((rv) => ({
      key: rv.id, api: "ptStar.rival", width: 130, align: "right", tint: "rival", sub: t("pt.star.rivalSub"),
      title: <RivalHeader rival={rv} editable={editable} inUse={inUseRival === rv.id} onRename={onRenameRival} onRemove={onRemoveRival} />,
      cellClass: (r) => (cellBad(r, rv.id) ? "!bg-danger-50" : undefined),
      render: (r) => (
        <CellInput exact value={r.rivals[rv.id] ?? null} disabled={!editable} placeholder={t("pt.star.pending")}
          onChange={(v) => onRivalPrice(rv.id, r.star, v)} />
      ),
    })),
    {
      key: "check", width: 90, title: t("pt.col.check"), sub: t("pt.col.check.sub"),
      render: (r) => {
        const c = checkOf(r);
        return <CheckCell ok={c.ok} lines={[c.ok ? t("check.pass") : t("check.fail"), reasonOf(r)]} />;
      },
    },
    {
      key: "skin", api: "ptStar.skin", title: t("pt.col.skin"), width: 210, align: "center",
      render: (r) => {
        const inherit = r.skinUrl ? null : skinOf(stars, r.star);
        return (
          <span className="flex items-center justify-center gap-1.5">
            {r.skinUrl
              ? <Thumb src={r.skinUrl} title={`#${r.star}`} className="h-9 w-9 rounded-sm border border-line object-cover" />
              : inherit
                ? <Thumb src={inherit.url} title={`#${inherit.from}`} className="h-9 w-9 rounded-sm border border-dashed border-line object-cover opacity-70" />
                : <span className="text-danger text-[11px]">{t("pt.skinMissing")}</span>}
            <span className="flex flex-col gap-0.5">
              <Button size="sm" disabled={!editable || up.busy}
                onClick={() => up.pick(r.star, (url, star) => onPatch(star, { skinUrl: url }))}>
                {r.skinUrl ? t("card.reupload") : t("card.upload")}
              </Button>
              {r.skinUrl
                ? (
                  <Button size="sm" disabled={!editable || up.busy} onClick={() => onPatch(r.star, { skinUrl: "" })}>
                    {t("level.imgClear")}
                  </Button>
                )
                : inherit
                  ? <span className="text-[11px] text-ink-placeholder">{t("pt.skinInherit", { n: inherit.from })}</span>
                  : null}
            </span>
          </span>
        );
      },
    },
    {
      key: "unlocks", api: "ptSlot.unlockStar", title: t("pt.col.unlocks"), width: 200,
      render: (r) => {
        const hit = slots.filter((s) => s.unlockStar === r.star);
        return hit.length
          ? (
            <span className="flex flex-wrap gap-1">
              {hit.map((s) => <Tag key={s.slot} tone="success">{s.slot} · {s.nameI18n.en ?? ""}</Tag>)}
            </span>
          )
          : dash;
      },
    },
  ];

  const badRows = stars.filter((r) => starIssue(r) != null).length;

  return (
    <div>
      {econ && <div className="mb-2"><EconModelNotice model={econ.model} scale={econ.scale} legacyEMilli={econ.legacyEMilli} /></div>}
      <Toolbar
        left={(
          <span className="flex items-center gap-2 text-xs">
            <span className="text-ink-placeholder">{t("pt.jump")}</span>
            <Input value={jump} size="sm" className="w-20" placeholder="1"
              onChange={(v) => { setJump(v); if (v !== "") scrollToStar(Number(v)); }} />
            <span className="text-ink-placeholder">{t("pt.range", { n: stars.length })}</span>
          </span>
        )}
      />

      {up.error && <div className="mb-3"><Notice tone="danger">{up.error}</Notice></div>}
      {badRows > 0 && <div className="mb-3"><Notice tone="danger">{t("pt.badRows", { n: badRows })}</Notice></div>}

      <Panel>
        <SectionTitle
          extra={<Tag>{t("pt.star.head", { n: fmt(stars.length), total: fmt(total) })}{missing && kind !== "formula" ? `（${t("pt.star.missingN", { n: missing })}）` : ""}</Tag>}
        >
          {t("nav.pettown-star")}
        </SectionTitle>
        <Notice tone="info">{t(econ?.model === 2 ? "pt.star.noteG" : "pt.star.note")}</Notice>
        <div className="mt-3">
          <CostSourceBar api="cost.source" value={costSource} rivals={rivals} editable={editable} onChange={onSource} onAddRival={onAddRival}
            failed={failed} count={stars.length} unit="pettownStar" />
        </div>
        <div className="my-3 flex flex-wrap items-center gap-2 text-xs">
          <Button size="sm" tone={view==="cost"?"primary":undefined} onClick={()=>setView("cost")}>{t("cost.view.cost")}</Button>
          {/* 单掷金币版本没有 B：不给「B 值明细」视图 */}
          {econ?.model !== 2 && <Button size="sm" tone={view==="b"?"primary":undefined} onClick={()=>setView("b")}>{t("cost.view.b")}</Button>}
          <span>{t("pt.econ.range")}</span><Input value={from} onChange={setFrom} size="sm" className="w-20" placeholder={t("pt.econ.from")} />~<Input value={to} onChange={setTo} size="sm" className="w-20" placeholder={t("pt.econ.to")} />
          <label className="flex items-center gap-1"><input type="checkbox" checked={onlyBad} onChange={e=>setOnlyBad(e.target.checked)} />{t("cost.onlyFailed")}</label>
          <Button size="sm" onClick={()=>{setFrom("");setTo("");setOnlyBad(false);}}>{t("pt.econ.reset")}</Button>
          <span>{shown.length}/{stars.length}</span>
        </div>
        <div ref={tableRef}>
        <DataTable columns={showB ? [columns[0],
          {key:"algorithmB",title:t("pt.econ.algorithmB"),align:"right",width:160,render:r=><Amount v={r.algorithmB??null} />},
          {key:"runtimeB",title:t("pt.econ.runtimeB"),align:"right",width:160,render:r=><Amount v={r.runtimeB??null} />},
          ...incomeSeries.map<Column<PettownStarView>>(series=>({key:series.id,title:series.source==="formula"?t("pt.star.formula"):series.source==="manual"?t("pt.star.manual"):rivals.find(rv=>rv.id===series.source)?.name??series.source,align:"right",width:160,render:r=><Amount v={series.points[r.star-1]?.value??null} />})),
          {key:"rolls",title:t("pt.col.rolls"),align:"right",width:160,render:r=><Amount v={r.rolls} />},
          {key:"estimate",title:t("pt.econ.estimate"),align:"right",width:160,render:r=><Amount v={r.estimate??null} />},
        ] : columns} rows={shown} rowKey={(r) => r.star} lensRow={(r) => r.star} fixedLayout stickyHeader virtual
          minWidth={columns.reduce((a, c) => a + (typeof c.width === "number" ? c.width : 100), 0)}
          empty={t("pt.star.empty")}
          rowTint={(r) => (starIssue(r) ? "bg-danger-50/40" : undefined)}
          summary={showB ? undefined : {
            star: <span>{t("cost.total")}<span className="block text-[11px] font-normal text-ink-placeholder">{t("pt.allStars", { n: stars.length })}</span></span>,
            rolls: (
              <div className="flex flex-col items-end">
                <Amount v={rollsTotal} />
                <span className="text-[11px] font-normal text-ink-placeholder">
                  {changedRolls ? t("pt.rolls.changedN", { n: changedRolls }) : t("pt.rolls.allDefault")}
                </span>
              </div>
            ),
            formula: <Amount className="text-ink-secondary" v={sumOf((r) => r.formula)} />,
            manual: overlaySum((r) => r.manual),
            ...Object.fromEntries(rivals.map((rv) => [rv.id, overlaySum((r) => r.rivals[rv.id])])),
          }} />
        </div>
        <p className="mt-3 text-xs leading-relaxed text-ink-placeholder">{t("pt.star.foot")}</p>
        {up.input}
      </Panel>
    </div>
  );
}
